import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import PageHeader from "../components/PageHeader";
import DataTable from "../components/DataTable";
import TableToolbar from "../components/TableToolbar";
import Toast, { useToast } from "../components/Toast";
import { obtenerOrdenes, cancelarOrden } from "../services/ordenesServicioService";

const STATUS_BADGE = {
  A: { label: "Activa",    bg: "#dbeafe", color: "#1d4ed8" },
  C: { label: "Cancelada", bg: "#fee2e2", color: "#dc2626" },
  T: { label: "Terminada", bg: "#dcfce7", color: "#16a34a" },
};

const fmt = (d) => d ? new Date(d).toLocaleDateString("es-MX", { day: "2-digit", month: "short", year: "numeric" }) : "";
const money = (n) => n != null ? Number(n).toLocaleString("es-MX", { style: "currency", currency: "MXN" }) : "—";

export default function OrdenesServicioListaPage() {
  const navigate = useNavigate();
  const { toasts, closeToast, success: ok, error: err } = useToast();

  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [reloadKey, setReloadKey] = useState(0);
  const reload = () => setReloadKey(k => k + 1);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await obtenerOrdenes(search);
      setRows(data ?? []);
    } catch {
      err("Error al cargar órdenes");
    } finally {
      setLoading(false);
    }
  }, [search, reloadKey]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { load(); }, [load]);

  const handleCancelar = async (id) => {
    if (!window.confirm("¿Cancelar esta orden de servicio?")) return;
    try {
      await cancelarOrden(id);
      ok("Orden cancelada");
      reload();
    } catch (e) {
      err(e?.response?.data || "No se pudo cancelar");
    }
  };

  const columns = [
    { key: "ordenTrabajoId", title: "Folio", style: { width: 70, textAlign: "center" } },
    { key: "fechaEntrada", title: "Entrada", render: r => fmt(r.fechaEntrada), style: { width: 110 } },
    { key: "fechaEntrega", title: "Entrega", render: r => fmt(r.fechaEntrega), style: { width: 110 } },
    { key: "nombreCliente", title: "Cliente" },
    { key: "contacto", title: "Contacto", style: { width: 130 } },
    { key: "trabajo", title: "Trabajo", style: { maxWidth: 200 } },
    { key: "total", title: "Total", render: r => money(r.total), style: { width: 110, textAlign: "right" } },
    {
      key: "tieneFirma", title: "Firma",
      render: r => r.tieneFirma
        ? <span style={{ color: "#16a34a", fontWeight: 700, fontSize: 13 }}>✓ Firmada</span>
        : <span style={{ color: "#9ca3af", fontSize: 13 }}>—</span>,
      style: { width: 90, textAlign: "center" },
    },
    {
      key: "status", title: "Estatus",
      render: r => {
        const s = STATUS_BADGE[r.status] ?? STATUS_BADGE.A;
        return <span style={{ padding: "2px 10px", borderRadius: 12, fontSize: 12, fontWeight: 600, background: s.bg, color: s.color }}>{s.label}</span>;
      },
      style: { width: 90 },
    },
    {
      key: "_acc", title: "",
      render: r => (
        <div style={{ display: "flex", gap: 4, flexWrap: "wrap" }}>
          <button className="lmd-btn lmd-btn-xs lmd-btn-outline"
            onClick={() => navigate(`/servicios/ordenes/${r.ordenTrabajoId}/editar`)}>
            {r.status === "A" ? "Editar" : "Ver"}
          </button>
          <button className="lmd-btn lmd-btn-xs lmd-btn-outline"
            onClick={() => navigate(`/servicios/ordenes/${r.ordenTrabajoId}/print`)}>
            Imprimir
          </button>
          {r.status === "A" && !r.tieneFirma && (
            <button className="lmd-btn lmd-btn-xs lmd-btn-primary"
              onClick={() => navigate(`/servicios/ordenes/${r.ordenTrabajoId}/firma`)}>
              Firma
            </button>
          )}
          {r.status === "A" && (
            <button className="lmd-btn lmd-btn-xs lmd-btn-danger-outline"
              onClick={() => handleCancelar(r.ordenTrabajoId)}>
              Cancelar
            </button>
          )}
        </div>
      ),
      style: { width: 200 },
    },
  ];

  return (
    <div>
      <Toast toasts={toasts} onClose={closeToast} />
      <PageHeader title="Órdenes de Servicio" subtitle="Control de trabajos y firmas de clientes" />

      <TableToolbar
        search={search}
        onSearch={setSearch}
        extraRight={
          <button className="lmd-btn lmd-btn-primary" onClick={() => navigate("/servicios/ordenes/nueva")}>
            + Nueva Orden
          </button>
        }
      />

      <DataTable columns={columns} rows={rows} keyField="ordenTrabajoId" loading={loading} />
    </div>
  );
}
